import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Shell from '@/components/Shell';
import Login from '@/pages/Login';
import Verify from '@/pages/Verify';
import Apply from '@/pages/Apply';
import Dashboard from '@/pages/Dashboard';
import Students from '@/pages/Students';
import Student360 from '@/pages/Student360';
import RegistrationDesk from '@/pages/RegistrationDesk';
import Gradebook from '@/pages/Gradebook';
import Admissions from '@/pages/Admissions';
import Academics from '@/pages/Academics';
import Attendance from '@/pages/Attendance';
import Fees from '@/pages/Fees';
import Comms from '@/pages/Comms';
import Admin from '@/pages/Admin';
import PaymentSuccess from '@/pages/PaymentSuccess';
import Onboarding from '@/pages/Onboarding';
import Profile from '@/pages/Profile';
import Iam from '@/pages/Iam';
import Lifecycle from '@/pages/Lifecycle';
import Operations from '@/pages/Operations';
import Chat from '@/pages/Chat';
import Announcements from '@/pages/Announcements';
import Exams from '@/pages/Exams';
import Library from '@/pages/Library';
import Lms from '@/pages/Lms';
import University from '@/pages/University';
import Transport from '@/pages/Transport';
import Cctv from '@/pages/Cctv';
import Bgv from '@/pages/Bgv';
import Directory from '@/pages/Directory';
import Hr from '@/pages/Hr';
import Notifications from '@/pages/Notifications';
import Events from '@/pages/Events';
import Alumni from '@/pages/Alumni';
import Advancement from '@/pages/Advancement';
import Give from '@/pages/Give';
import Rsvp from '@/pages/Rsvp';
import Procurement from '@/pages/Procurement';
import VendorPortal from '@/pages/VendorPortal';
import IdentityGraph from '@/pages/IdentityGraph';
import Health from '@/pages/Health';
import Hostel from '@/pages/Hostel';
import Assets from '@/pages/Assets';
import Integrations from '@/pages/Integrations';
import Automation from '@/pages/Automation';
import DocsCenter from '@/pages/DocsCenter';
import Reports from '@/pages/Reports';
import NHI from '@/pages/NHI';
import ITOps from '@/pages/ITOps';
import ITSM from '@/pages/ITSM';
import Signup from '@/pages/Signup';
import ResetPassword, { ForgotPassword } from '@/pages/ResetPassword';
import Reconciliation from '@/pages/Reconciliation';
import AcademicCore from '@/pages/AcademicCore';
import Advisor from '@/pages/Advisor';
import CourseRegistration from '@/pages/CourseRegistration';
import DirectorySync from '@/pages/DirectorySync';
import Insights from '@/pages/Insights';
import { I18nProvider } from '@/lib/i18n';

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono-label text-xs uppercase">Loading ONRID...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/apply/:code" element={<Apply />} />
            <Route path="/give/:code" element={<Give />} />
            <Route path="/rsvp/:code" element={<Rsvp />} />
            <Route path="/vendor-portal" element={<VendorPortal />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/students" element={<Protected><Students /></Protected>} />
            <Route path="/students/:id" element={<Protected><Student360 /></Protected>} />
            <Route path="/admissions" element={<Protected><Admissions /></Protected>} />
            <Route path="/academics" element={<Protected><Academics /></Protected>} />
            <Route path="/attendance" element={<Protected><Attendance /></Protected>} />
            <Route path="/fees" element={<Protected><Fees /></Protected>} />
            <Route path="/transport" element={<Protected><Transport /></Protected>} />
            <Route path="/cctv" element={<Protected><Cctv /></Protected>} />
            <Route path="/bgv" element={<Protected><Bgv /></Protected>} />
            <Route path="/comms" element={<Protected><Comms /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/iam" element={<Protected><Iam /></Protected>} />
            <Route path="/directory-sync" element={<Protected><DirectorySync /></Protected>} />
            <Route path="/insights" element={<Protected><Insights /></Protected>} />
            <Route path="/procurement" element={<Protected><Procurement /></Protected>} />
            <Route path="/identity-graph" element={<Protected><IdentityGraph /></Protected>} />
            <Route path="/lifecycle" element={<Protected><Lifecycle /></Protected>} />
            <Route path="/operations" element={<Protected><Operations /></Protected>} />
            <Route path="/chat" element={<Protected><Chat /></Protected>} />
            <Route path="/announcements" element={<Protected><Announcements /></Protected>} />
            <Route path="/exams" element={<Protected><Exams /></Protected>} />
            <Route path="/library" element={<Protected><Library /></Protected>} />
            <Route path="/lms" element={<Protected><Lms /></Protected>} />
            <Route path="/university" element={<Protected><University /></Protected>} />
            <Route path="/profile" element={<Protected><Profile /></Protected>} />
            <Route path="/directory" element={<Protected><Directory /></Protected>} />
            <Route path="/hr" element={<Protected><Hr /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/events" element={<Protected><Events /></Protected>} />
            <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/automation" element={<Protected><Automation /></Protected>} />
            <Route path="/docs" element={<Protected><DocsCenter /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/nhi" element={<Protected><NHI /></Protected>} />
            <Route path="/itops" element={<Protected><ITOps /></Protected>} />
            <Route path="/itsm" element={<Protected><ITSM /></Protected>} />
            <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
            <Route path="/academic-core" element={<Protected><AcademicCore /></Protected>} />
            <Route path="/advisor" element={<Protected><Advisor /></Protected>} />
            <Route path="/register" element={<Protected><CourseRegistration /></Protected>} />
            <Route path="/registration-desk" element={<Protected><RegistrationDesk /></Protected>} />
            <Route path="/gradebook" element={<Protected><Gradebook /></Protected>} />
            <Route path="/alumni" element={<Protected><Alumni /></Protected>} />
            <Route path="/advancement" element={<Protected><Advancement /></Protected>} />
            <Route path="/health" element={<Protected><Health /></Protected>} />
            <Route path="/hostel" element={<Protected><Hostel /></Protected>} />
            <Route path="/assets" element={<Protected><Assets /></Protected>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}
